/* eslint-disable max-len */
import React, { useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Modal, Box, Grid } from '@mui/material';
import { sendRequest } from '../../api/shootAPI';
import API from '../../api/endpoints';
import { UserContext } from '../../contexts/User';
import ProjectSelector from '../../components/selectors/ProjectSelector';
import { STYLED_SELECT_CLASS, STYLED_SELECT_STYLE } from '../../utils/styledSelect';
import { FALLBACK_DEPARTMENTS, FALLBACK_DESIGNATIONS } from '../../constants/memberFallbacks';

const TEXT_INPUT_CLASS = 'w-full h-10 px-3 border border-rule rounded-[8px] text-sm bg-white text-ink-900 focus:outline-none focus:border-brand-500';

const FORM_DEFAULT = {
  email: '', accountType: '', department: '', designation: '', selectedProjects: [],
};

const accountTypes = [
  { label: 'Team member' },
  { label: 'Team Leader' },
  { label: 'Workspace owner' },
  { label: 'Workspace admin' },
];

function FieldLabel({ children }) {
  return <span className="block text-sm font-medium text-ink-900 mb-1.5">{children}</span>;
}
FieldLabel.propTypes = { children: PropTypes.node.isRequired };

function NewMemberModal({
  closeModal, getMembers, reportingManagers, departments, designations,
}) {
  const { activeWorkspace } = useContext(UserContext);
  const [formData, setFormData] = useState(FORM_DEFAULT);
  const [formErrors, setFormErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState();
  const [projects, setProjects] = useState([]);
  // eslint-disable-next-line
  const [projectsLoading, setProjectsLoading] = useState(false);

  const departmentOptions = departments.length ? departments.map((d) => d.name) : FALLBACK_DEPARTMENTS;
  const designationOptions = designations.length ? designations.map((d) => d.name) : FALLBACK_DESIGNATIONS;

  const handleForm = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const handleProjectSelection = (selectedProjects) => {
    setFormData({ ...formData, selectedProjects });
  };

  const handleAddMember = async () => {
    setFormErrors({});

    try {
      const memberResponse = await sendRequest({
        method: 'post',
        url: API.WORKSPACE.member(activeWorkspace.id),
        data: {
          email: formData.email,
          accountType: formData.accountType,
          department: formData.department,
          designation: formData.designation,
          reportingManager: formData.reportingManager,
        },
      });

      if (memberResponse.success) {
        const newMemberId = memberResponse.data.user.id;
        if (formData.selectedProjects.length > 0 && newMemberId) {
          const projectIds = formData.selectedProjects.map((project) => project.id);
          await sendRequest({
            method: 'post',
            url: API.WORKSPACE.addToProjects(activeWorkspace.id, newMemberId),
            data: { projectIds },
          });
        }

        setSuccessMessage(`Created new member. Invitation sent to ${formData.email}`);
        setFormData(FORM_DEFAULT);
        getMembers();
        closeModal();
      } else {
        setFormErrors(memberResponse.data);
      }
    } catch (error) {
      console.error('Error adding member:', error);
    }
  };

  useEffect(() => {
    const getProjects = async () => {
      if (activeWorkspace) {
        setProjectsLoading(true);
        try {
          const response = await sendRequest({
            method: 'get',
            url: API.WORKSPACE.getProjects(activeWorkspace.id),
          });

          if (response.success && response.data?.data && Array.isArray(response.data.data)) {
            const projectsArray = response.data.data;

            const formattedProjects = projectsArray.map((project) => ({
              id: project.id,
              name: project.name || `Project ${project.id}`,
              description: project.description || '',
              workspaceId: project.workspaceId,
              ...project,
            }));

            setProjects(formattedProjects);
          } else {
            setProjects([]);
          }
        } catch (error) {
          setProjects([]);
        } finally {
          setProjectsLoading(false);
        }
      }
    };

    getProjects();
  }, [activeWorkspace]);

  return (
    <Modal open onClose={closeModal} className="flex-row align-c jstfy-c" data-testid="new-member-modal">
      <Box style={{
        width: 600,
        maxWidth: 'calc(100% - 32px)',
        maxHeight: '90vh',
        overflowY: 'auto',
        padding: 24,
        background: '#fff',
        borderRadius: 12,
      }}
      >
        <h5 className="text-lg font-semibold leading-none text-ink-900 mb-5" data-testid="modal-title">
          Add a New Workspace Member
        </h5>

        <Grid container rowSpacing={2.5} columnSpacing={2}>
          {/* Email Field */}
          <Grid item xs={12}>
            <FieldLabel>Email Address</FieldLabel>
            <input
              required
              type="email"
              name="email"
              placeholder="name@company.com"
              value={formData?.email}
              onChange={(event) => handleForm('email', event.target.value)}
              data-testid="email"
              className={TEXT_INPUT_CLASS}
              style={{ borderStyle: 'solid' }}
            />
            {formErrors?.email && (
              <span className="block text-xs mt-1" style={{ color: '#CC4525' }}>{formErrors.email}</span>
            )}
          </Grid>

          {/* Account Type Field */}
          <Grid item xs={12}>
            <FieldLabel>Account Type</FieldLabel>
            <select
              value={formData.accountType}
              onChange={(e) => handleForm('accountType', e.target.value)}
              data-testid="account-type"
              className={`${STYLED_SELECT_CLASS} w-full`}
              style={STYLED_SELECT_STYLE}
            >
              <option value="" disabled>Select account type</option>
              {accountTypes.map((t) => <option key={t.label} value={t.label}>{t.label}</option>)}
            </select>
          </Grid>

          {/* Reporting Manager Field */}
          <Grid item xs={12}>
            <FieldLabel>Reporting To</FieldLabel>
            <select
              value={formData.reportingManager || ''}
              onChange={(e) => setFormData({ ...formData, reportingManager: e.target.value ? Number(e.target.value) : '' })}
              data-testid="reporting-manager"
              className={`${STYLED_SELECT_CLASS} w-full`}
              style={STYLED_SELECT_STYLE}
            >
              <option value="">Select a manager</option>
              {reportingManagers.map((m) => (
                <option key={m.id} value={m.id}>{`${m.user.firstName} ${m.user.lastName}`}</option>
              ))}
            </select>
          </Grid>

          {/* Department Field */}
          <Grid item xs={12} sm={6}>
            <FieldLabel>Department</FieldLabel>
            <select
              value={formData.department}
              onChange={(e) => handleForm('department', e.target.value)}
              data-testid="department"
              className={`${STYLED_SELECT_CLASS} w-full`}
              style={STYLED_SELECT_STYLE}
            >
              <option value="" disabled>Select department</option>
              {departmentOptions.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </Grid>

          {/* Designation Field */}
          <Grid item xs={12} sm={6}>
            <FieldLabel>Designation</FieldLabel>
            <select
              value={formData.designation}
              onChange={(e) => handleForm('designation', e.target.value)}
              data-testid="designation"
              className={`${STYLED_SELECT_CLASS} w-full`}
              style={STYLED_SELECT_STYLE}
            >
              <option value="" disabled>Select designation</option>
              {designationOptions.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </Grid>

          {/* Project Selection Field */}
          <Grid item xs={12}>
            <ProjectSelector
              label="Projects"
              options={projects}
              selected={formData.selectedProjects}
              onChange={handleProjectSelection}
              placeholder={projects.length === 0 ? 'No projects available' : 'Select projects...'}
            />
          </Grid>
        </Grid>

        {/* Success/Error Messages */}
        {formErrors.general && (
          <div className="text-sm mt-4" style={{ color: '#CC4525' }}>
            {formErrors.general}
          </div>
        )}
        {successMessage && <div className="text-sm mt-4" style={{ color: '#53A551' }}>{successMessage}</div>}

        {/* Action Buttons */}
        <div className="flex justify-start items-center gap-2 mt-6">
          <button
            type="button"
            onClick={closeModal}
            data-testid="cancel"
            className="inline-flex items-center justify-center px-4 py-2 border border-rule rounded-[8px] text-sm font-medium text-ink-900 bg-white hover:bg-gray-50 transition"
            style={{ borderStyle: 'solid' }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleAddMember}
            disabled={!(formData?.email && formData?.accountType)}
            data-testid="add-member"
            className="inline-flex items-center justify-center px-4 py-2 bg-[#53A551] text-white rounded-[8px] text-sm font-semibold hover:bg-[#458a44] disabled:opacity-40 disabled:cursor-not-allowed transition"
          >
            Add Member
          </button>
        </div>
      </Box>
    </Modal>
  );
}

NewMemberModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
  getMembers: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  reportingManagers: PropTypes.array.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  departments: PropTypes.array,
  // eslint-disable-next-line react/forbid-prop-types
  designations: PropTypes.array,
};

NewMemberModal.defaultProps = {
  departments: [],
  designations: [],
};

export default NewMemberModal;
